import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Send, Sparkles, AlertCircle, Copy, Check } from "lucide-react";
import type { Recommendation, ChatMessage } from "@/types/artie-score";
import { apiClient } from "@/lib/api";
import ReactMarkdown from "react-markdown";

interface GoDeeperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recommendation: Recommendation;
  projectId: string;
  onUpdate: () => void;
}

export const GoDeeperDialog = ({
  open,
  onOpenChange,
  recommendation,
  projectId,
  onUpdate,
}: GoDeeperDialogProps) => {
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>(
    recommendation.chat_messages || []
  );
  const [tokenUsage, setTokenUsage] = useState(recommendation.token_usage || 0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const TOKEN_LIMIT = 10000;
  const remainingTokens = TOKEN_LIMIT - tokenUsage;
  const tokenPercentage = (tokenUsage / TOKEN_LIMIT) * 100;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage("");
    setError(null);
    setIsLoading(true);

    // Optimistically add user message
    const newUserMessage: ChatMessage = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setChatHistory(prev => [...prev, newUserMessage]);

    try {
      const response = await apiClient.goDeeperChat(projectId, {
        recommendation_id: recommendation.id || recommendation.what_to_improve,
        message: userMessage,
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: response.response,
        timestamp: new Date().toISOString(),
      };
      setChatHistory(prev => [...prev, assistantMessage]);
      setTokenUsage(response.total_token_usage);

      // Don't trigger parent update here - it causes page refresh
      // Parent will update when dialog closes
    } catch (err: any) {
      setError(err.message || "Failed to send message");
      // Remove optimistic user message on error
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (content: string, index: number) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden bg-gray-50">
        <div className="p-6 border-b bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Go Deeper: {recommendation.what_to_improve}
            </DialogTitle>
            <DialogDescription>
              Explore options and land on an approach with AI guidance.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Token Usage Bar */}
        <div className="bg-white border-b px-6 py-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Token Usage</span>
            <span className={tokenPercentage > 90 ? "text-red-600 font-semibold" : ""}>
              {tokenUsage.toLocaleString()} / {TOKEN_LIMIT.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                tokenPercentage > 90 ? "bg-red-600" : tokenPercentage > 70 ? "bg-amber-600" : "bg-blue-600"
              }`}
              style={{ width: `${Math.min(tokenPercentage, 100)}%` }}
            />
          </div>
          {tokenPercentage > 90 && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Token limit nearly reached
            </p>
          )}
        </div>

        {/* Chat History */}
        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          <div className="space-y-6 max-w-3xl mx-auto">
            {/* Context Card */}
            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-100 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-purple-100 rounded-md">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-purple-900">Recommendation Context</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">Why it matters</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{recommendation.why_it_matters}</p>
                </div>
                
                <div>
                  <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">How to improve</h4>
                  <div className="text-sm text-gray-700 prose prose-sm max-w-none prose-p:leading-relaxed prose-li:my-0.5">
                    <ReactMarkdown>{recommendation.how_to_improve}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>

            {/* Empty State with Prompt Examples */}
            {chatHistory.length === 0 && (
              <div className="py-8">
                <div className="text-center mb-8">
                  <p className="text-sm text-muted-foreground">Select a prompt below or type your own question to get started</p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    "Can you give me a step-by-step implementation plan?",
                    "What are the potential risks or downsides?",
                    "Can you provide a code example for this?",
                    "How can I measure the success of this change?",
                    "Explain this concept in more detail.",
                    "What are the industry best practices for this?"
                  ].map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setMessage(prompt);
                        // Optional: auto-focus the textarea
                        const textarea = document.querySelector('textarea');
                        if (textarea) textarea.focus();
                      }}
                      className="text-left p-4 rounded-xl border bg-white hover:border-purple-300 hover:shadow-md hover:bg-purple-50 transition-all group"
                    >
                      <p className="text-sm font-medium text-gray-700 group-hover:text-purple-700">{prompt}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-4 shadow-sm relative group ${
                    msg.role === "user"
                      ? "bg-purple-600 text-white rounded-br-none"
                      : "bg-white text-gray-900 border border-gray-100 rounded-bl-none"
                  }`}
                >
                  {msg.role === "user" ? (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-gray-900 prose-p:text-gray-800 prose-li:text-gray-800 prose-strong:text-gray-900 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                  
                  {msg.role === "assistant" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity bg-white border shadow-sm hover:bg-gray-50"
                      onClick={() => handleCopy(msg.content, idx)}
                      title="Copy to clipboard"
                    >
                      {copiedIndex === idx ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3 text-gray-500" />
                      )}
                    </Button>
                  )}

                  <p className={`text-[10px] mt-2 opacity-70 ${msg.role === "user" ? "text-purple-100" : "text-gray-500"}`}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Input Area */}
        <div className="p-6 bg-white border-t">
          <div className="max-w-3xl mx-auto space-y-3">
            <div className="flex gap-3 relative">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask for guidance, explore options, or describe your approach..."
                className="flex-1 min-h-[50px] max-h-[200px] resize-none pr-12 py-3 rounded-xl border-gray-200 focus:border-purple-300 focus:ring-purple-100"
                disabled={isLoading || remainingTokens <= 0}
              />
              <Button
                onClick={handleSend}
                disabled={!message.trim() || isLoading || remainingTokens <= 0}
                className="absolute right-2 bottom-2 h-8 w-8 p-0 rounded-lg bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                size="sm"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <p className="text-[11px] text-muted-foreground text-center">
              Press <kbd className="px-1 py-0.5 rounded bg-gray-100 border text-gray-500 font-sans">Enter</kbd> to send, <kbd className="px-1 py-0.5 rounded bg-gray-100 border text-gray-500 font-sans">Shift+Enter</kbd> for new line
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};